import gsap, { reduced } from "@lib/gsap";
import { Scroll } from "@lib/scroll";
import { onMount, onDestroy } from "@/modules/_";

const CMX_NAV_SCROLL_THRESHOLD = 600;

export default function (element: HTMLElement, _dataset: DOMStringMap) {
  const navMenu = element.querySelector<HTMLElement>(".nav-menu");
  const dropdowns = Array.from(element.querySelectorAll<HTMLElement>("[data-nav-dropdown]"));
  const overlay = element.querySelector<HTMLElement>(".nav-dropdown_overlay");
  const mobileBtn = element.querySelector<HTMLElement>("[data-nav-mobile-btn]");
  const skipLink = document.getElementById("skip-link");

  const isCmxNav = element.classList.contains("cc-cmx");
  let removeCmxScrollSub: (() => void) | undefined;
  let cmxInitialTop = "";

  const isMobile = () => window.innerWidth <= 991;
  const closeTimers = new Map<HTMLElement, ReturnType<typeof setTimeout>>();

  const navIsOpen = () => element.classList.contains("cc-open");

  function setRootNavOpen(open: boolean) {
    if (open) {
      element.classList.add("cc-open");
      element.setAttribute("data-nav-open", "");
    } else {
      element.classList.remove("cc-open");
      element.removeAttribute("data-nav-open");
    }
  }

  function handleSkipLink(e: MouseEvent | KeyboardEvent) {
    if (e.type === "keydown" && (e as KeyboardEvent).key !== "Enter") return;
    e.preventDefault();
    const target = document.querySelector<HTMLElement>("main");
    if (!target) return;
    target.setAttribute("tabindex", "-1");
    target.focus();
  }

  function openDropdown(dropdown: HTMLElement) {
    const content = dropdown.querySelector<HTMLElement>("[data-nav-content]");
    const links = dropdown.querySelectorAll<HTMLElement>(".nav-dropdown_link");
    if (!content) return;

    if (closeTimers.has(dropdown)) {
      clearTimeout(closeTimers.get(dropdown));
      closeTimers.delete(dropdown);
    }

    gsap.killTweensOf(content);
    gsap.killTweensOf(links);
    dropdown.classList.add("cc-open");

    if (isMobile()) {
      gsap.set(content, { display: "flex", height: 0, opacity: 1, y: 0 });
      gsap.to(content, {
        height: "auto",
        duration: 0.4,
        ease: "power2.out",
      });
      gsap.to(links, { opacity: 1, y: 0, duration: 0.6, ease: "power2.out", stagger: 0.075, delay: 0.1 });
    } else {
      overlay?.classList.add("cc-open");
      gsap.set(content, { display: "flex" });
      gsap.to(content, { opacity: 1, y: 0, duration: 0.4, ease: "power2.out" });
      gsap.to(links, { opacity: 1, y: 0, duration: 0.6, ease: "power2.out", stagger: 0.075, delay: 0.1 });
    }
  }

  function closeDropdown(dropdown: HTMLElement, options: { keepOverlay?: boolean; immediate?: boolean } = {}) {
    const { keepOverlay = false, immediate = false } = options;
    const content = dropdown.querySelector<HTMLElement>("[data-nav-content]");
    const links = dropdown.querySelectorAll<HTMLElement>(".nav-dropdown_link");
    if (!content) return;

    gsap.killTweensOf(content);
    gsap.killTweensOf(links);
    dropdown.classList.remove("cc-open");

    if (isMobile()) {
      gsap.set(content, { overflow: "hidden" });
      gsap.to(content, {
        height: 0,
        duration: 0.4,
        ease: "power2.inOut",
        onComplete: () => {
          gsap.set(content, { display: "none", height: "auto", overflow: "visible" });
          gsap.set(links, { opacity: 0, y: 4 });
        },
      });
    } else {
      const hasOtherOpenDropdown = dropdowns.some((item) => item !== dropdown && item.classList.contains("cc-open"));
      if (!keepOverlay && !hasOtherOpenDropdown) overlay?.classList.remove("cc-open");

      if (immediate) {
        gsap.set(content, { display: "none", opacity: 0, y: 6 });
        gsap.set(links, { opacity: 0, y: 4 });
        return;
      }

      gsap.to(content, {
        opacity: 0,
        y: 6,
        duration: 0.3,
        ease: "power2.out",
        onComplete: () => {
          if (!dropdown.classList.contains("cc-open")) {
            gsap.set(content, { display: "none" });
            gsap.set(links, { opacity: 0, y: 4 });
          }
        },
      });
    }
  }

  function closeAll() {
    dropdowns.forEach((dropdown) => {
      if (closeTimers.has(dropdown)) {
        clearTimeout(closeTimers.get(dropdown));
        closeTimers.delete(dropdown);
      }
      closeDropdown(dropdown);
    });
    overlay?.classList.remove("cc-open");
  }

  function closeMobileNav() {
    if (!navMenu) return;
    closeAll();
    gsap.to(navMenu, {
      opacity: 0,
      y: -8,
      duration: 0.25,
      ease: "power2.in",
      onComplete: () => {
        setRootNavOpen(false);
        document.body.style.overflow = "";
        gsap.set(navMenu, { clearProps: "opacity,y" });
      },
    });
  }

  function handleMobileBtn() {
    if (!navMenu) return;
    if (navIsOpen()) {
      closeMobileNav();
    } else {
      setRootNavOpen(true);
      document.body.style.overflow = "hidden";
      gsap.fromTo(navMenu, { opacity: 0, y: -8 }, { opacity: 1, y: 0, duration: 0.35, ease: "power2.out" });
    }
  }

  function handleKeydown(e: KeyboardEvent) {
    if (e.key === "Escape" && isMobile() && navIsOpen()) {
      closeMobileNav();
    }
  }

  function handleOutsideClick(e: MouseEvent | TouchEvent) {
    if (!isMobile() || !navIsOpen()) return;
    if (!element.contains(e.target as Node)) {
      closeMobileNav();
    }
  }

  function initCmxNavStickyTop() {
    if (!isCmxNav) return;

    const computedTop = getComputedStyle(element).top;
    if (!computedTop || computedTop === "auto") return;

    cmxInitialTop = computedTop;

    let docked = Scroll.scroll >= CMX_NAV_SCROLL_THRESHOLD;
    gsap.killTweensOf(element);
    gsap.set(element, { top: docked ? 0 : cmxInitialTop });

    function tweenCmxTop(nextDocked: boolean) {
      gsap.killTweensOf(element);
      gsap.to(element, {
        top: nextDocked ? 0 : cmxInitialTop,
        duration: reduced ? 0 : 0.55,
        ease: "power3.out",
        overwrite: true,
      });
    }

    removeCmxScrollSub = Scroll.add(() => {
      const nextDocked = Scroll.scroll >= CMX_NAV_SCROLL_THRESHOLD;
      if (nextDocked === docked) return;
      docked = nextDocked;
      tweenCmxTop(nextDocked);
    });
  }

  function destroyCmxNavStickyTop() {
    removeCmxScrollSub?.();
    removeCmxScrollSub = undefined;
    if (!isCmxNav || !cmxInitialTop) return;
    gsap.killTweensOf(element);
    gsap.set(element, { clearProps: "top" });
  }

  const mouseenterHandlers = new Map<HTMLElement, () => void>();
  const mouseleaveHandlers = new Map<HTMLElement, () => void>();

  dropdowns.forEach((dropdown) => {
    const onEnter = () => {
      if (isMobile()) return;

      closeTimers.forEach((timer) => clearTimeout(timer));
      closeTimers.clear();

      dropdowns.forEach((item) => {
        if (item !== dropdown && item.classList.contains("cc-open")) {
          closeDropdown(item, { keepOverlay: true, immediate: true });
        }
      });

      openDropdown(dropdown);
    };

    const onLeave = () => {
      if (isMobile()) return;
      const timer = setTimeout(() => closeDropdown(dropdown), 100);
      closeTimers.set(dropdown, timer);
    };

    mouseenterHandlers.set(dropdown, onEnter);
    mouseleaveHandlers.set(dropdown, onLeave);
  });

  const triggerClickHandlers = new Map<HTMLElement, (e: Event) => void>();

  dropdowns.forEach((dropdown) => {
    const trigger = dropdown.querySelector<HTMLElement>("[data-nav-trigger]");
    if (!trigger) return;

    const onClick = (e: Event) => {
      if (!isMobile()) return;
      e.preventDefault();
      e.stopPropagation();
      const isOpen = dropdown.classList.contains("cc-open");
      closeAll();
      if (!isOpen) openDropdown(dropdown);
    };

    triggerClickHandlers.set(trigger, onClick);
  });

  onMount(() => {
    if (overlay) document.body.appendChild(overlay);

    dropdowns.forEach((dropdown) => {
      const content = dropdown.querySelector<HTMLElement>("[data-nav-content]");
      const links = dropdown.querySelectorAll<HTMLElement>(".nav-dropdown_link");
      if (content) gsap.set(content, { display: "none", opacity: 0, y: 6 });
      if (links.length) gsap.set(links, { opacity: 0, y: 4 });
    });

    skipLink?.addEventListener("click", handleSkipLink);
    skipLink?.addEventListener("keydown", handleSkipLink);

    dropdowns.forEach((dropdown) => {
      dropdown.addEventListener("mouseenter", mouseenterHandlers.get(dropdown)!);
      dropdown.addEventListener("mouseleave", mouseleaveHandlers.get(dropdown)!);
    });

    triggerClickHandlers.forEach((handler, trigger) => {
      trigger.addEventListener("click", handler);
    });

    mobileBtn?.addEventListener("click", handleMobileBtn);
    document.addEventListener("keydown", handleKeydown);
    document.addEventListener("click", handleOutsideClick);
    document.addEventListener("touchstart", handleOutsideClick);

    initCmxNavStickyTop();
  });

  onDestroy(() => {
    destroyCmxNavStickyTop();

    closeTimers.forEach((timer) => clearTimeout(timer));
    closeTimers.clear();

    skipLink?.removeEventListener("click", handleSkipLink);
    skipLink?.removeEventListener("keydown", handleSkipLink);

    dropdowns.forEach((dropdown) => {
      dropdown.removeEventListener("mouseenter", mouseenterHandlers.get(dropdown)!);
      dropdown.removeEventListener("mouseleave", mouseleaveHandlers.get(dropdown)!);
    });

    triggerClickHandlers.forEach((handler, trigger) => {
      trigger.removeEventListener("click", handler);
    });

    mobileBtn?.removeEventListener("click", handleMobileBtn);
    document.removeEventListener("keydown", handleKeydown);
    document.removeEventListener("click", handleOutsideClick);
    document.removeEventListener("touchstart", handleOutsideClick);

    document.body.style.overflow = "";
    setRootNavOpen(false);
  });
}
